import type { QueryKey } from '@tanstack/react-query';

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../components/generic/Pagination/consts';
import usePaginationState from '../../../components/generic/Pagination/usePaginationState';
import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import type { PaginatedRequestQuery } from '../../../../shared/types/routes/pagination';
import type { GetTeamMembersResponse } from '../../../../shared/types/routes/teams';

type GetTeamMembersParams = PaginatedRequestQuery & {
  search?: string;
};

/**
 * Function to fetch team members
 */
function fetchTeamMembers(teamId: number, params: GetTeamMembersParams) {
  const searchParams = new URLSearchParams({
    page: (params?.page ?? DEFAULT_PAGE).toString(),
    pageSize: (params?.pageSize ?? DEFAULT_PAGE_SIZE).toString(),
  });

  if (params.search) {
    searchParams.append('search', params.search);
  }

  return get<GetTeamMembersResponse>(`/api/teams/${teamId}/members?${searchParams.toString()}`);
}

/**
 * Base query key for team members query. This can be used to invalidate ALL pages
 * of team members for a given team.
 */
export const getBaseTeamMembersQueryKey = (teamId: number): QueryKey => ['teams', teamId, 'members'];

/**
 * Full query key for team members query
 */
export const getTeamMembersQueryKey = (teamId: number, params: GetTeamMembersParams): QueryKey => [
  ...getBaseTeamMembersQueryKey(teamId),
  params,
];

/**
 * Hook for fetching team members. Bring your own pagination state.
 *
 * @param {number} teamId - ID of the team to fetch members for
 * @param {GetTeamMembersParams} params - Pagination and search parameters
 * @param {boolean} shouldFetch - Whether the query should automatically run
 */
export function useTeamMembers(teamId: number, params: GetTeamMembersParams, shouldFetch: boolean = true) {
  return useQuery<GetTeamMembersResponse>({
    queryKey: getTeamMembersQueryKey(teamId, params),
    queryFn: () => fetchTeamMembers(teamId, params),
    enabled: shouldFetch,
    // Keeps previous data visible while fetching next page
    keepPreviousData: true,
  });
}

const DEFAULT_INITIAL_PARAMS: GetTeamMembersParams = {
  search: '',
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
};

/**
 * Hook for fetching team members with pagination and optional search.
 */
export default function usePaginatedTeamMembers(
  teamId: number,
  params: GetTeamMembersParams = DEFAULT_INITIAL_PARAMS,
  shouldFetch: boolean = true,
) {
  const { page, pageSize, setPage, setPageSize } = usePaginationState(params.page, params.pageSize);
  const query = useTeamMembers(
    teamId,
    { page, pageSize, search: params.search },
    shouldFetch,
  );

  return {
    ...query,
    page,
    pageSize,
    setPage,
    setPageSize,
  };
}
